import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IFeaturesService,
  IOpsViewerMessagesService,
  IApplicationContextService
} from '../../../services/services';
//import { CompanyNameViewModel } from '../../../models/models';
import { Title } from '@angular/platform-browser';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { IUserManagerService } from '../../../services/iUserManager.service';
import { finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { CompanyClient, CompanyNameViewModel, CompanyRole, User, UserClient } from '../../../services/nswag/nswagclient';
import {  UserRoles } from '../../../models/models';

type MessageSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';

interface UserDetailMessage {
  severity: MessageSeverity;
  summary?: string;
  detail?: string;
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'user-detail',
  
  standalone: false,
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.css']
})
export class UserDetailComponent implements OnInit, OnDestroy {
  public user: User = new User();
  protected isNew :boolean = false;
  public userEmailValid = true;


  public companies: SelectItem[] = [];
  public selectedCompanies: string[] = [];

  public roles: SelectItem[] = [];


  public messages: UserDetailMessage[] = [];
  public modelInvalid = false;
  public showModelInvalidDialog = false;

  public saving = false;

  protected $rolesToDisplay: Observable<CompanyRole[]> = of([]);
  protected $user!: Observable<User>;
  protected $companies: Observable<SelectItem[]> = of([]);
  protected $authenticatedUser: Observable<User | null> = of(null);
  protected $canAddCompanyRole: Observable<boolean> = of(false);
  public isAuthenticatedUserGlobalAdmin: Observable<boolean> = of(false);
  protected $addRoleAction: ReplaySubject<number> = new ReplaySubject<number>(1);
  private $companyContext!: Observable<CompanyNameViewModel | null>;
  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _userClient: UserClient,
    private _userManagerService: IUserManagerService,
    private _companyService: CompanyClient,
    private _featureService: IFeaturesService,
    private _titleService: Title,
    private _messageService: IOpsViewerMessagesService,
    private _applicationContextService: IApplicationContextService,
    private _confirmationService: ConfirmationService
  ) {
    this.user.companyRoles = this.user.companyRoles ?? [];
  }

  ngOnInit() {
    this._applicationContextService.hideApplicationRequestsBar();
    this.getRoles();
    this.$companies = this._companyService.get().pipe(map(c => {
      return c.map<SelectItem>(r => {
        return {
          label: r.name,
          value: r.id
        }
      }
      );
    }));
    //this.$companies.subscribe(r=>console.warn(r));
    this.$authenticatedUser = this._userManagerService.getCurrentUser();
    this.isAuthenticatedUserGlobalAdmin = this.$authenticatedUser.pipe(map(u => !!u?.isGlobalAdmin));
    this.$companyContext = this._applicationContextService.companyContextObserver;
    this.$user = this.getUser();
    this.$rolesToDisplay = combineLatest([this.$user, this.$authenticatedUser, this.$addRoleAction]).pipe(map(r => {
      return this.filterCompanyRoles(r[1], r[0]);
    }), shareReplay(1));
    this.$addRoleAction.next(234); //inital fire for combine to work above

    this.$canAddCompanyRole = combineLatest([this.$authenticatedUser, this.$companyContext]).pipe(
      map(([u, c]) => {
        if (!c?.companyId) {
          return false;
        }
        return this.isAuthenticatedUserACompanyAdmin2(u, c.companyId) &&
          !(this.user.companyRoles ?? []).some(r => r.companyId === c.companyId);
      })
    );



    let isEditSelf$ = combineLatest([this.$user, this.$authenticatedUser]).pipe(map(u=>{
      return u[0].id === u[1]?.id;
    }));

    isEditSelf$.pipe(first()).subscribe(e=>{
      if (e){
        this._applicationContextService.showApplicationRequestsBar();
      }
    });


  }

  getUser():Observable<User> {
    return this._route.params.pipe(switchMap(params => {
      let userId = params['id'];
      if (!userId || userId.toUpperCase() === 'NEW') {
        this.isNew = true; //side effect here I don't like
        return this.getNewUserFromCompanyContext();
      } else {
        return this._userClient.getById(userId);
      }
    }), switchMap(u => {
      return this.$companies.pipe(map(companies => {
        UserDetailComponent.removeRolesForInvalidCompanies(u, companies);
        console.warn(u);
        return u;
      }));
    }), tap(u => { 
      this.user = u;
      this.setPageTitle(u.email);
    }), shareReplay(1));
  }

  getNewUserFromCompanyContext(): Observable<User> {
    return this.$companyContext.pipe(map(c => {
      const user = new User();
      user.companyRoles = [];
      //this.isNew //side effect;
      if (c?.companyId) {
        const newRole = new CompanyRole();
        newRole.companyId = c.companyId;
        newRole.role = UserRoles.reader;
        user.companyRoles.push(newRole);
      }
      return user;
    }));
  }


  ngOnDestroy() {
    this._applicationContextService.hideApplicationRequestsBar();
  }

  setPageTitle(userId: string | undefined) {
    if (!userId || userId === 'New' || userId === '') {
      this._titleService.setTitle('New User');
    } else { this._titleService.setTitle(`edit ${userId}`); }
  }


  private static removeRolesForInvalidCompanies(user: User, companies: SelectItem[]) {
    const validCompanyIds = companies.map(c => c.value).filter((id): id is string => typeof id === 'string');
    user.companyRoles = (user.companyRoles ?? []).filter(r => validCompanyIds.includes(r.companyId ?? ''));
  }


  //#region Company Role Management
  /*
  Rules for displaying which roles can be seen and edited
    1.  If Authenticated User is viewing self or is Global Admin,
        view all roles in read only mode (doesn't matter company context setting)
    2.  If Authenticated User is Company Admin,
        a. Show only role applicable to current company context.
        b. CompanyId is read only, but role value can be edited.
        c. If role does not exist for current company context, Add new role option is available which follows same
           rules in b once added.
        d. Has ability to remove company role from user.
    3.  Any other Authenticated User cannot view any role information of the user
  */

  getRoles() {
    this.roles.push({ label: UserRoles.admin, value: UserRoles.admin, icon: 'fa fa-shield' });
    this.roles.push({ label: UserRoles.editor, value: UserRoles.editor, icon: 'far fa-edit' });
    this.roles.push({ label: UserRoles.reader, value: UserRoles.reader, icon: 'fa fa-book' });
  }


  isAuthenticatedUserACompanyAdmin2(authenticatedUser: User | null, companyId: string | undefined): boolean {
    //console.warn('asdf',authenticatedUser);
    if (!authenticatedUser || !companyId) { return false; }
    if (!authenticatedUser.companyRoles) { return false; }
    return !!authenticatedUser.isGlobalAdmin ||
      authenticatedUser.companyRoles.some(r => r.companyId === companyId && r.role === UserRoles.admin);
  }


  

  addDisplayedCompanyRole() {
   

    const c = this._applicationContextService.getCompanyContext();

    console.warn(c);
    if (!c?.companyId) {
      this._messageService.warnMessage('Cannot add role without selected company context', '');
      return;
    }
    if (!(this.user.companyRoles ?? []).some(r => r.companyId === c.companyId)) {
      const newRole = new CompanyRole();
      newRole.companyId = c.companyId;
      newRole.role = UserRoles.reader;
      (this.user.companyRoles ??= []).push(newRole);

    } else {
      this._messageService.warnMessage('Current selected Company role already exists on this user', '');
    }
    this.$addRoleAction.next(234);



    //}
  }

  deleteRole(role: CompanyRole) {
    if (this.user.companyRoles) {
      this.user.companyRoles = this.user.companyRoles.filter(r => r.companyId !== role.companyId);
    } else {
      this.user.companyRoles = [];
    }
    this.$addRoleAction.next(234);
  }


  //#endregion


  validateEmail() {
    // tslint:disable-next-line:max-line-length
    this.userEmailValid = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(this.user.email ?? '');
  }

  validateUserModel(): boolean {
    if (!this.user.email || !this.userEmailValid) { return false; }
    if (!this.user.firstName) { return false; }
    if (!this.user.lastName) { return false; }
    // if(!this.user.contactNumber) return false;

      if (!this.user.companyRoles) {
        this.user.companyRoles = [];
      }
      const hasInvalidRole = this.user.companyRoles.some(r => !r.companyId || !r.role);
      if (hasInvalidRole) { return false; }
    

    return true;
  }

  saveUser() {
    this.normalizeUserDefaults();

    this.showModelInvalidDialog = this.modelInvalid = !this.validateUserModel();
    if (this.showModelInvalidDialog) {
      this.stopSavingOperation();
      return;
    }

    const saveRequest$ = this.getSaveUserRequest();
    if (!saveRequest$) {
      this.stopSavingOperation();
      return;
    }

    this.startSavingOperation();
    saveRequest$.pipe(
      finalize(() => this.stopSavingOperation())
    ).subscribe({
      next: () => {
        this._applicationContextService.alertUserDetailsUpdated();
        this.navigateAfterCreateOrUpdate();
      },
      error: error => {
        this._messageService.errorMessage(error, 'Could not get user');
      }
    });
  }

  private normalizeUserDefaults() {
    if (this.user.isEmployee === undefined || this.user.isEmployee == null) { this.user.isEmployee = false; }
    if (this.isNew && (this.user.hasAllCompany == null || this.user.hasAllCompany === undefined)) {
      this.user.hasAllCompany = false;
    }
  }

  private getSaveUserRequest(): Observable<any> | null {
    if (this.isNew) {
      return this._userClient.post(this.user);
    }

    if (!this.user.id) {
      this._messageService.errorMessage('Missing user id', 'Could not update user');
      return null;
    }

    return this._userClient.put(this.user.id, this.user);
  }

  private startSavingOperation() {
    this.saving = true;
    this._applicationContextService.showApplicationSpinner(false);
  }

  private stopSavingOperation() {
    this._applicationContextService.hideApplicationSpinner();
    this.saving = false;
  }

  closeInvalidModelDialog() {
    this.showModelInvalidDialog = false;
  }

  navigateAfterCreateOrUpdate() {

    this.$authenticatedUser.pipe(first()).subscribe(u=>{
      if (this.user.id && this.user.id === u?.id) {
        this.navigateHome();
      } else {
        this.returnToUserList();
      }
    });

    
  }
  returnToUserList() {
    this._router.navigate(['/users/']);
  }

  navigateHome() {
    this._router.navigate(['/home']);
  }

  confirmDelete() {
    if (!this.user.id) {
      return;
    }
    this._confirmationService.confirm(
      {
        key: 'confirmationDialog',
        header: 'Delete Confirmation',
        message: 'Are you sure you want to delete ' + this.user.firstName + ' ' + this.user.lastName + '?',
        icon: 'fa fa-question-circle',
        accept: () => { this.deleteUser(this.user.id); }// ,
        // reject: () => { }
      }
    );
  }

  deleteUser(userId: string | undefined) {

    if (this.isNew || !userId) {
      this._router.navigate(['/users']);
      return;
    }

    this._userClient.delete(userId).subscribe(
      result => {
        this._messageService.infoMessage('User deleted', '');
        this._applicationContextService.alertUserDetailsUpdated();
        this._router.navigate(['/users']);
      },
      error => {
        this._messageService.errorMessage(error, 'Could not get user');
        this.saving = false;
      }
    );
  }

  filterCompanyRoles(authenticatedUser: User | null, user: User): CompanyRole[] {
    if (!user || !user.companyRoles || !authenticatedUser) {
      return [];
    }

    if (authenticatedUser.isGlobalAdmin || authenticatedUser.id === user.id) {
      return user.companyRoles;
    }

    if (!authenticatedUser.companyRoles) {
      return [];
    }

    return user.companyRoles.filter(r =>
      authenticatedUser.companyRoles?.some(ar => ar.companyId === r.companyId && ar.role === UserRoles.admin) ?? false
    );
  }

  copyEmailToClipboard() {
    if (this.user?.email) {
      navigator.clipboard.writeText(this.user.email)
        .then(() => {
          this._messageService.infoMessage('Email copied to clipboard', '');
        })
        .catch(() => {
          this._messageService.errorMessage('Failed to copy email', '');
        });
    }
  }
}
